"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { useMediaPicker } from "../media/MediaPickerHostContext";
type LibraryMediaRecord = {
  alt: string;
  title: string;
};
const libraryRecordCache = new Map<string, LibraryMediaRecord>();

/**
 * File Manager title + alt for a media id. `found` is false until a row loads,
 * and stays false when the id is missing or the fetch returns nothing.
 */
export function useLibraryMediaRecord(mediaId) {
  const $ = _c(16);
  const id = mediaId?.trim() ?? "";
  const {
    fetchMediaDetails
  } = useMediaPicker();
  const cached = id && libraryRecordCache.has(id) ? libraryRecordCache.get(id) : null;
  let t0;
  if ($[0] !== cached || $[1] !== id) {
    t0 = () => ({
      id,
      ready: !id || cached != null,
      record: cached ?? null
    });
    $[0] = cached;
    $[1] = id;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const [state, setState] = useState(t0);
  if (state.id !== id) {
    setState({
      id,
      ready: !id || cached != null,
      record: cached ?? null
    });
  }
  let t1;
  if ($[3] !== cached || $[4] !== id || $[5] !== state) {
    t1 = state.id === id ? state : {
      id,
      ready: !id || cached != null,
      record: cached ?? null
    };
    $[3] = cached;
    $[4] = id;
    $[5] = state;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const current = t1;
  let t2;
  let t3;
  if ($[7] !== fetchMediaDetails || $[8] !== id) {
    t2 = () => {
      if (!id) {
        return;
      }
      if (libraryRecordCache.has(id)) {
        setState({
          id,
          ready: true,
          record: libraryRecordCache.get(id) ?? null
        });
        return;
      }
      if (!fetchMediaDetails) {
        setState({
          id,
          ready: true,
          record: null
        });
        return;
      }
      let cancelled = false;
      setState({
        id,
        ready: false,
        record: null
      });
      fetchMediaDetails(id).then(row => {
        if (cancelled) {
          return;
        }
        if (!row) {
          setState({
            id,
            ready: true,
            record: null
          });
          return;
        }
        const next = {
          alt: row.alt?.trim() ?? "",
          title: row.title?.trim() ?? ""
        };
        libraryRecordCache.set(id, next);
        setState({
          id,
          ready: true,
          record: next
        });
      });
      return () => {
        cancelled = true;
      };
    };
    t3 = [fetchMediaDetails, id];
    $[7] = fetchMediaDetails;
    $[8] = id;
    $[9] = t2;
    $[10] = t3;
  } else {
    t2 = $[9];
    t3 = $[10];
  }
  useEffect(t2, t3);
  const t4 = current.record?.alt ?? "";
  const t5 = current.record !== null;
  const t6 = current.record?.title ?? "";
  let t7;
  if ($[11] !== current.ready || $[12] !== t4 || $[13] !== t5 || $[14] !== t6) {
    t7 = {
      alt: t4,
      found: t5,
      ready: current.ready,
      title: t6
    };
    $[11] = current.ready;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}